import { useEffect } from "react";
import { MaskedLines } from "@/components/Reveal";
import { Link } from "react-router-dom";

// Sets document title + meta description for SEO per page.
export const usePageMeta = (title, description, { noindex = false } = {}) => {
  useEffect(() => {
    document.title = title;
    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) { robots = document.createElement("meta"); robots.setAttribute("name", "robots"); document.head.appendChild(robots); }
      robots.setAttribute("content", "noindex");
    } else if (robots) robots.remove();
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description || "");
    // Per-page canonical + og:url (Cloudflare serves inner pages with a trailing slash)
    const path = window.location.pathname.replace(/\/?$/, "/");
    const url = "https://basscrane.com" + (path === "/" ? "/" : path);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", url);
    const og = document.querySelector('meta[property="og:url"]');
    if (og) og.setAttribute("content", url);
    const ogt = document.querySelector('meta[property="og:title"]');
    if (ogt) ogt.setAttribute("content", title);
    const tw = document.querySelector('meta[name="twitter:title"]');
    if (tw) tw.setAttribute("content", title);
  }, [title, description, noindex]);
};

// Shared inner-page hero band.
// With `image`: full-bleed photo behind a navy gradient, white text.
// Without: the original light band.
export const PageHero = ({ eyebrow, titleLines = [], intro, image, imageAlt = "", testId }) => {
  const dark = Boolean(image);
  return (
    <section
      className={`relative overflow-hidden border-b ${
        dark ? "pt-20 md:pt-[120px] h-[560px] md:h-[720px] flex flex-col justify-center" : "pt-44 md:pt-52 pb-16 md:pb-24"
      } ${
        dark ? "bg-[#0F1D45] text-white border-[#0F1D45]" : "bg-[#F7F8FA] border-[#E5E7EB]"
      }`}
      data-testid={testId}
    >
      {dark && (
        <>
          {/* Image fills only the visible band below the fixed site header: 1920x600 at desktop */}
          <div className="absolute inset-x-0 bottom-0 top-20 md:top-[120px]">
            <img
              src={image}
              alt={imageAlt}
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover object-[65%_center]"
            />
            {/* Left-heavy navy wash for headline legibility; lighter on the right so the photo reads */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1D45]/90 via-[#0F1D45]/50 to-[#0F1D45]/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1D45]/60 via-transparent to-transparent" />
          </div>
        </>
      )}

      <div className={`absolute top-0 left-0 h-full w-px ml-6 md:ml-12 ${dark ? "bg-white/25" : "bg-[#1C3172]/40"}`} />

      <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <nav className={`font-mono-plex text-sm uppercase tracking-widest mb-8 ${dark ? "text-white/70" : "text-[#5B6270]"}`}>
          <Link to="/" className={`transition-colors duration-300 ${dark ? "hover:text-white" : "hover:text-[#1C3172]"}`}>Home</Link>
          <span className={`mx-2 ${dark ? "text-[#9FB4E8]" : "text-[#1C3172]"}`}>/</span>
          <span className={dark ? "text-white" : "text-[#1A1A1A]"}>{eyebrow}</span>
        </nav>
        <h1 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase max-w-5xl ${dark ? "leading-[0.92]" : ""} ${dark ? "text-white" : "text-[#1A1A1A]"}`}>
          <MaskedLines lines={titleLines} lineClassName={dark ? "text-white" : ""} />
        </h1>
        {intro && (
          <p className={`mt-6 max-w-2xl text-base md:text-lg leading-relaxed ${dark ? "text-white/85" : "text-[#5B6270]"}`}>
            {intro}
          </p>
        )}
      </div>
    </section>
  );
};
